import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { Text } from "@origin-digital/style-guide";
import { Stack, Heading } from "@origin-digital/ods-core";
import { findDOMNode } from "react-dom";
import { parseMarkdownEvent } from "@origin-digital/event-dispatcher";
// import { logger } from "@origin-digital/reporting-client";

interface IArticleBody {
  title: string;
  body: string;
}

const ArticleContent = styled.div`
  a {
    color: inherit;

    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  }

  h1,
  h2,
  h3,
  h4 {
    font-size: 16px;
  }

  table {
    border-collapse: collapse;
    border: 1px solid ${({ theme }) => theme.colors.grey600};
  }

  td {
    vertical-align: top;
    text-align: left;
    padding: 5px;
    p {
      margin-block-start: 0px;
      margin-block-end: 0px;
    }
  }

  blockquote {
    padding: 10px;
    background-color: #f6f6f6;
    border-left: 10px;
    border-left-style: solid;
    border-left-color: #d0d0d0;
    margin-block-start: 0;
    margin-block-end: 0;
    margin-inline-start: 0;
    margin-inline-end: 0;

    h3 {
      margin-top: 0px;
      margin-bottom: 4px;
    }

    p {
      margin-top: 4px;
      margin-bottom: 4px;
    }
  }
`;

const aHrefRegDoubleQuotes = /(<a[^>]* )href="(NAV_TO:[^"]*)"([^>]*>)/g;
const aHrefRegSingleQuotes = /(<a[^>]* )href='(NAV_TO:[^']*)'([^>]*>)/g;

const ATTR_NAME = "data-navto";

export const replaceNavToLinks = (html: string): string => {
  html = html.replace(aHrefRegDoubleQuotes, `$1 ${ATTR_NAME}="$2" href="#" $3`);
  html = html.replace(aHrefRegSingleQuotes, `$1 ${ATTR_NAME}="$2" href="#" $3`);
  return html;
};

// modifies the markup coming out of the cms so it looks more appealing
const massageMarkup = (htmlContent: string): string => {
  return htmlContent?.replace(/h2>/gi, "h3>");
};

export const replaceHtml = (html: string): string => {
  if (!html) return html;
  html = replaceNavToLinks(html);
  html = massageMarkup(html);
  return html;
};

export const ArticleBody = ({ body, title }: IArticleBody) => {
  const articleRef = useRef(null);

  useEffect(() => {
    const cleanUp: (() => void)[] = [];
    if (articleRef && body) {
      // eslint-disable-next-line react/no-find-dom-node
      const dom = findDOMNode(articleRef.current) as Element;
      const elmsList = dom?.querySelectorAll(`a[${ATTR_NAME}]`);
      const elms: Element[] = [].slice.call(elmsList);
      elms.forEach((elm) => {
        const navToMd = elm.getAttribute(ATTR_NAME);
        if (navToMd) {
          // const dispatchEvent = parseMarkdownEvent(navToMd, logger.error);
          const dispatchEvent = parseMarkdownEvent(navToMd, true).onClick;
          if (dispatchEvent) {
            const cbFn = (e: Event) => {
              e.preventDefault();
              dispatchEvent();
            };
            elm.addEventListener("click", cbFn);
            cleanUp.push(() => elm.removeEventListener("click", cbFn));
          }
        }
      });
    }
    return () => {
      cleanUp.forEach((fn) => fn());
    };
  }, [articleRef, body]); // needs to re-run on new body

  return (
    <Stack
      space={{
        sm: "xlarge",
        xs: "small",
      }}
    >
      <Heading component={"h1"} variant="h3">
        {title}
      </Heading>
      <Text>
        <ArticleContent
          ref={articleRef}
          dangerouslySetInnerHTML={{ __html: replaceHtml(body) }}
        />
      </Text>
    </Stack>
  );
};
